import { queryOptions } from '@tanstack/react-query';

import { fetchBackend } from './apiClient';

export type GcpServiceAccountToCreate = {
  serviceAccountJson?: string;
  projectId?: string;
};

type GcpServiceAccountStatus = 'active' | 'inactive' | 'error';

export type GcpServiceAccount = {
  id: string;
  jsonProjectId: string;
  clientEmail: string;
  clientId: string;
  privateKeyId: string;
  universeDomain: string;
  status: GcpServiceAccountStatus;
  createdAt: string;
  updatedAt: string;
  // Frontend-specific fields for compatibility
  accountId: string; // Same as id
  projectId: string; // The Anyshift project ID, not GCP project ID
};

export type GcpServiceAccountForList = {
  name: string; // Contains clientEmail (service account email)
  id: string;
  projectId: string;
  status: GcpServiceAccountStatus;
};

export const gcpServiceAccountQueries = {
  all: () => ['gcp-service-accounts'],
  byProject: (projectId: string) => [
    ...gcpServiceAccountQueries.all(),
    projectId,
  ],
  list: (projectId: string) =>
    queryOptions({
      queryKey: [...gcpServiceAccountQueries.byProject(projectId), 'list'],
      queryFn: async () => {
        try {
          return await listGcpServiceAccounts(projectId);
        } catch (error) {
          console.warn(
            'GCP service accounts endpoint not implemented yet:',
            error,
          );

          return [];
        }
      },
    }),
  details: (projectId: string) => [
    ...gcpServiceAccountQueries.byProject(projectId),
    'details',
  ],
  detail: (projectId: string, accountId: string) =>
    queryOptions({
      queryKey: [...gcpServiceAccountQueries.details(projectId), accountId],
      queryFn: async () => {
        return await getGcpServiceAccount(projectId, accountId);
      },
    }),
} as const;

export const upsertGcpServiceAccount = async (
  projectId: string,
  payload: GcpServiceAccountToCreate,
  accountId?: string,
): Promise<GcpServiceAccountToCreate> => {
  if (accountId !== undefined) {
    const updateResponse = await fetchBackend(
      `/gcp/service-accounts/${projectId}/${accountId}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );

    if (!updateResponse.ok) {
      const errorData = (await updateResponse.json()) as { message?: string };
      throw new Error(
        errorData.message ?? `Error: ${updateResponse.statusText}`,
      );
    }

    return payload;
  }

  const createResponse = await fetchBackend(
    `/gcp/service-accounts/${projectId}`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );

  if (!createResponse.ok) {
    const errorData = (await createResponse.json()) as { message?: string };
    throw new Error(errorData.message ?? `Error: ${createResponse.statusText}`);
  }

  return payload;
};

export const getGcpServiceAccount = async (
  projectId: string,
  accountId: string,
): Promise<GcpServiceAccount> => {
  const getResponse = await fetchBackend(
    `/gcp/service-accounts/${projectId}/${accountId}`,
    {
      method: 'GET',
    },
  );

  if (!getResponse.ok) {
    throw new Error(`Error: ${getResponse.statusText}`);
  }

  const data = (await getResponse.json()) as {
    gcpCredentials: {
      id: string;
      serviceAccountName: string;
      jsonProjectId: string;
      clientEmail: string;
      clientId: string;
      privateKeyId: string;
      universeDomain: string;
      status: GcpServiceAccountStatus;
      createdAt: string;
      updatedAt: string;
    };
  };

  // Map backend fields to frontend fields
  return {
    ...data.gcpCredentials,
    accountId: data.gcpCredentials.id,
    projectId, // The Anyshift project ID from the URL
  };
};

export const listGcpServiceAccounts = async (
  projectId: string,
): Promise<GcpServiceAccountForList[]> => {
  const getResponse = await fetchBackend(`/gcp/service-accounts/${projectId}`, {
    method: 'GET',
  });

  if (!getResponse.ok) {
    throw new Error(`Error: ${getResponse.statusText}`);
  }

  const data = (await getResponse.json()) as {
    gcpServiceAccounts?: Array<{
      id: string;
      name: string;
      projectId: string;
      status: GcpServiceAccountStatus;
    }> | null;
  };

  // Always return an array, even if backend returns null or undefined
  return data.gcpServiceAccounts ?? [];
};

export const deleteGcpServiceAccount = async (
  projectId: string,
  accountId: string,
): Promise<void> => {
  const deleteResponse = await fetchBackend(
    `/gcp/service-accounts/${projectId}/${accountId}`,
    {
      method: 'DELETE',
    },
  );

  if (!deleteResponse.ok) {
    throw new Error(`Error: ${deleteResponse.statusText}`);
  }
};
